/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import {
  ArrowRight,
  ShieldCheck,
  Zap,
  ChevronDown,
  Building2,
  Sparkles,
  PlugZap,
  Plug,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { type EREProvider } from '../types';
import { Filters } from './Filters';
import { useImageMappings } from '../hooks/useImageMappings';
import { getCompareAnchor, scrollToCompare } from '../lib/compareAnchor';
import { COVERAGE_ANCHOR } from '../lib/orgIdentity';
import { formatInteger } from '../lib/formatters';
import { SURFACES, trackEvent } from '../lib/gtag';
import { IMAGE_SIZES, responsiveImageProps } from '../lib/imageVariants.js';
import { providerHubPath } from '../lib/providerRoutes.js';
interface HeroProps {
  providerCount?: number;
  /** Passed straight through to the embedded <Filters> card, whose duration
   *  dropdown is derived from the rows rather than hardcoded. */
  providers?: EREProvider[];
}

export const Hero: React.FC<HeroProps> = ({ providerCount = 0, providers }) => {
  const { language, t } = useLanguage();
  const { getImage, getImageFocusStyle } = useImageMappings();
  const heroBg = getImage('hero', 'hero_bg');
  const compareHash = getCompareAnchor(language);

  // Every claim is on screen at once. It used to be a one-at-a-time rotator:
  // three of the four claims were invisible at any moment (and all four were
  // absent from the baked HTML except whichever happened to be index 0), so a
  // visitor — or a crawler — had to wait 10 seconds to read a trust bar.
  //
  // Since 2026-09-23 the claims are a quiet proof strip UNDER the buttons, not
  // two rows of uppercase pills above the eyebrow: stacked on top of eyebrow,
  // H1, euro line and subtitle, the pills were most of what made the hero feel
  // cramped. The copy still has to stay short ("85 aanbieders", "Dagelijks
  // gecheckt"): the four items share one row of the text column on a desktop
  // only while the word after the count stays at about 10 characters and the
  // other three at about 18 — the budgets the Homepage tab counts against
  // (src/pages/admin/copyBudgets.ts).
  const trustItemClass = 'inline-flex items-center gap-1.5 whitespace-nowrap';

  const trustEntries: Array<{
    key: string;
    available: boolean;
    icon: React.ReactNode;
    label: React.ReactNode;
    /** Optional destination — a claim that can be checked, links to the check. */
    href?: string;
  }> = [
    {
      key: 'count',
      available: providerCount > 0,
      icon: <Building2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />,
      // The NEa register holds roughly three times this number, and the two
      // pages never explained the gap — which reads as "the short list is the
      // paid one". The chip now leads straight to the explanation on
      // /providers, so the coverage claim is checkable from the place it
      // is made. The visible text is unchanged; the sr-only clause is what
      // gives the link a self-explanatory accessible name.
      href: `${providerHubPath(language)}#${COVERAGE_ANCHOR}`,
      label: (
        <span>
          {/* Locale-aware: a four-digit count is "1.234" in NL and "1,234" in
              EN. `.toString()` would print the same digits in both. */}
          <span className="text-white tabular-nums">{formatInteger(providerCount, language)}</span>{' '}
          {t('hero.trust_bar_providers_count')}
          <span className="sr-only"> — {t('hero.trust_bar_providers_coverage')}</span>
        </span>
      ),
    },
    {
      key: 'independent',
      available: true,
      icon: <ShieldCheck className="w-3.5 h-3.5 text-emerald-500 shrink-0" />,
      label: t('hero.trust_bar_independent'),
    },
    {
      key: 'free',
      available: true,
      icon: <Zap className="w-3.5 h-3.5 text-emerald-500 shrink-0" />,
      label: t('hero.trust_bar_free'),
    },
    {
      key: 'checked',
      available: true,
      // CSS pulse, not motion: motion's SSR pass serialises the first keyframe
      // as an inline `opacity`, which is exactly the baked-invisible state the
      // prerender contract test rejects. `animate-pulse` leaves the static HTML
      // fully opaque and only moves once a browser runs it.
      icon: (
        <span className="w-3.5 h-3.5 flex items-center justify-center shrink-0">
          <span className="block w-2 h-2 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.55)] animate-pulse motion-reduce:animate-none" />
        </span>
      ),
      // "Dagelijks gecheckt", not "bijgewerkt": the nightly page-watch job
      // hashes every provider price page each night. The data itself is edited
      // by hand, so a daily-update claim was an overclaim.
      label: t('hero.trust_bar_checked'),
    },
  ];

  const visibleEntries = trustEntries.filter((entry) => entry.available);

  return (
    <section className="relative overflow-hidden bg-transparent flex flex-col min-h-[calc(100svh-80px)] lg:min-h-[calc(100vh-80px)]">
      {/* Photographic backdrop — lowest layer. An <img> with srcset rather
          than a CSS background-image (2026-10-01): a background can only ever
          be one file, and the admin-uploaded hero on production is a 360 KB
          photo every phone downloaded in full. The candidates come from
          src/lib/imageVariants.js, the same list the server's preload tag
          names, and the `backdrop` sizes let a phone take the 480/768 px
          file for a photo it shows at a third of its opacity. The slow
          Ken Burns scale stays on the wrapper (desktop only), as a CSS
          animation — see the loop keyframes in src/index.css for why. */}
      {heroBg && (
        <div className="absolute inset-0 -z-20 pointer-events-none overflow-hidden">
          <div
            aria-hidden="true"
            className="absolute inset-0 opacity-35 dark:opacity-30 [html.light_&]:opacity-55 md:motion-safe:animate-ken-burns"
          >
            <img
              {...responsiveImageProps(heroBg, IMAGE_SIZES.backdrop)}
              alt=""
              fetchPriority="high"
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover object-center"
              style={getImageFocusStyle('hero', 'hero_bg')}
            />
          </div>
          {/* Radial vignette — fades to page background in both themes */}
          <div
            className="absolute inset-0"
            style={{
              background:
                'radial-gradient(ellipse at center, transparent 50%, var(--color-slate-950) 92%)',
            }}
          />
          {/* Emerald tint overlay — multiply darkens, hidden in light mode via CSS to keep SSR markup deterministic.
              Both blend layers are desktop-only (md): a mix-blend-mode layer the size of the hero makes a phone
              GPU re-blend the photo under it on every frame it moves, and at 35 % photo opacity over slate-950
              the tint and the grain are below what a phone screen shows anyway. */}
          <div className="hidden md:block absolute inset-0 bg-emerald-950/10 mix-blend-multiply [html.light_&]:hidden" />
          {/* Extra filmic grain on hero only — same: render unconditionally, hide in light mode */}
          <div
            className="hidden md:block absolute inset-0 opacity-[0.04] mix-blend-overlay [html.light_&]:hidden"
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
            }}
          />
        </div>
      )}

      {/* Background decoration — blur orbs sit above the photo. Desktop-only
          (md), like the card's own orbs below: two 384 px blur(64px) layers
          at 10 % opacity are invisible on a phone and expensive to rasterise. */}
      <div className="hidden md:block absolute top-0 left-1/2 -translate-x-1/2 w-full h-full -z-10 opacity-10 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-emerald-400 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-400 rounded-full blur-3xl" />
      </div>

      {/* w-full: this flex item is centred with auto margins, which sizes it to
          its content. The text column below is a size container, so it reports
          no natural width, and without w-full the grid shrank to the card's
          natural width (1263 instead of 1280 px in Dutch). */}
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex-grow flex items-center pt-6 pb-8 lg:pt-10 lg:pb-8">
        {/* Two equal columns, 48 px apart. Not a copy-heavy split: the filter
            card needs its ~576 px from 1280 up, and narrower its volume label
            truncates. The text column gets its room from the smaller gap and
            from a headline sized to the column (below). */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center w-full">
          {/* Entrance reveal is CSS, not motion: a motion `initial` serialises
              into the prerendered HTML as opacity:0, which would hide the H1
              from every non-JS reader. See .reveal-* in src/index.css.
              `@container`: the H1 is sized in cqw of THIS column. */}
          <div className="@container min-w-0 text-left reveal-left">
            {/* Audience clarifier — makes it explicit the platform is for
                people who own an EV charge point (particulier & zakelijk). */}
            <div className="inline-flex items-center gap-1.5 mb-4 md:mb-5 text-emerald-400 [html.light_&]:text-emerald-700 text-xs sm:text-sm font-black uppercase tracking-wider">
              <PlugZap className="w-4 h-4 shrink-0" aria-hidden="true" />
              {t('hero.audience_eyebrow')}
            </div>

            {/* Sized by the column, not the viewport. At a fixed 60 px from lg
                up, the live Dutch title's 21-letter "inboekdienstverleners"
                was 656 px wide in a 441–576 px column and ran into the filter
                card. 8.6cqw keeps a 21-letter word (≈ 0.52 em per letter in
                Inter 900 at this tracking) at 94 % of the column at every
                width, so a line holds at most ~22 characters (about 19 once
                words wrap) on a phone and a desktop alike. The CMS counter
                derives its word limit from the same two numbers
                (src/pages/admin/copyBudgets.ts). break-words is the net for a
                longer word: it wraps mid-word instead of overlapping the card. */}
            <h1 className="text-[length:clamp(1.5rem,8.6cqw,3.25rem)] font-black tracking-tight text-white mb-4 md:mb-5 leading-[1.05] text-balance break-words">
              {t('hero.title')}
            </h1>

            <p className="flex items-center gap-2 text-base sm:text-lg md:text-xl font-black text-emerald-400 [html.light_&]:text-emerald-700 mb-3 md:mb-4 tracking-tight leading-tight">
              <Sparkles className="w-5 h-5 shrink-0" aria-hidden="true" />
              {t('hero.save_up_to')}
            </p>

            {/* The mechanism in one sentence, right under the promise it
                justifies: what an ERE is, who has to buy it, who sells it for
                you, how you get paid. Nothing above the results said this;
                the site's own explanation sat in faq.a1, collapsed, after 25
                result cards. Plain t() text — no links, no HTML — so a CMS
                edit can never nest a <p> in this <p> (hydration #418). */}
            <p className="text-base md:text-lg text-slate-400 [html.light_&]:text-black max-w-xl mb-3 md:mb-4 leading-relaxed font-medium text-pretty">
              {t('hero.mechanism_line')}
            </p>

            <p className="text-base md:text-lg text-slate-400 [html.light_&]:text-black max-w-xl mb-7 md:mb-8 leading-relaxed font-medium text-pretty">
              {t('hero.subtitle')}
            </p>

            <div className="flex flex-wrap items-center gap-3 md:gap-4">
              <Link
                to={`/${language}?audience=Particulier#${compareHash}`}
                // No scrollTo here: the link carries `#vergelijken`, and the
                // router's scroll manager (ScrollToTop.tsx) brings the comparer
                // into view for every hash navigation — a second scroll from
                // the click used to race it with a different offset.
                onClick={() => {
                  trackEvent('hero_cta_click', {
                    variant: 'particulier',
                    surface: SURFACES.hero,
                    language,
                  });
                }}
                className="px-6 py-3 md:px-8 md:py-4 bg-emerald-700 text-white text-sm md:text-base font-black rounded-2xl border-2 border-emerald-700 hover:bg-emerald-800 hover:border-emerald-800 transition-all shadow-xl shadow-emerald-900/20 flex items-center justify-center gap-2 group"
              >
                {t('hero.cta_particulier')}
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to={`/${language}?audience=Zakelijk#${compareHash}`}
                onClick={() => {
                  trackEvent('hero_cta_click', {
                    variant: 'zakelijk',
                    surface: SURFACES.hero,
                    language,
                  });
                }}
                className="px-6 py-3 md:px-8 md:py-4 bg-slate-800 text-slate-200 text-sm md:text-base font-black rounded-2xl border-2 border-slate-700 hover:border-emerald-500 hover:text-emerald-500 transition-all flex items-center justify-center gap-2 group"
              >
                {t('hero.cta_zakelijk')}
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
            {/* The way out for a visitor without a charge point: the
                laadpaal-aanvraag on /geen-laadpaal. Until 2026-10-21 this link
                opened the kWh estimator, which the filters card still offers
                next to its volume field. Since 2026-10-22 it asks for a quote
                (no_charger_link_v2): nothing on the site orders a charge point. */}
            <Link
              to={`/${language}/geen-laadpaal`}
              onClick={() => {
                trackEvent('hero_cta_click', {
                  variant: 'no_charger',
                  surface: SURFACES.hero,
                  language,
                });
              }}
              className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-emerald-400 [html.light_&]:text-emerald-700 hover:text-emerald-300 underline underline-offset-4"
            >
              <Plug className="w-4 h-4" aria-hidden="true" />
              {t('hero.no_charger_link_v2')}
            </Link>

            {/* The proof strip (see trustItemClass for why it sits down here).
                Inside the text column on purpose: DOM order is visual order at
                every width, where a row across both columns would need CSS
                `order` on a phone, on which the card follows the copy. */}
            <ul
              aria-label={t('hero.trust_label')}
              className="mt-7 md:mt-8 pt-5 border-t border-white/10 flex flex-wrap items-center gap-x-4 gap-y-2 text-[13px] font-medium text-slate-300"
            >
              {visibleEntries.map((entry) => (
                <li key={entry.key} className={trustItemClass}>
                  {entry.href ? (
                    <Link
                      to={entry.href}
                      className="inline-flex items-center gap-1.5 rounded hover:text-emerald-400 hover:underline underline-offset-4 focus-ring transition-colors"
                    >
                      {entry.icon}
                      {entry.label}
                    </Link>
                  ) : (
                    <>
                      {entry.icon}
                      {entry.label}
                    </>
                  )}
                </li>
              ))}
            </ul>
          </div>

          <div
            className="relative w-full max-w-xl mx-auto lg:max-w-none reveal-right"
            style={{ animationDelay: '200ms' }}
          >
            <Filters providers={providers} />
            {/* Decorative elements — desktop only (blur-3xl is expensive on mobile GPUs) */}
            <div className="hidden md:block absolute -top-12 -right-12 w-64 h-64 bg-emerald-900/30 rounded-full blur-3xl -z-10" />
            <div className="hidden md:block absolute -bottom-12 -left-12 w-64 h-64 bg-blue-900/30 rounded-full blur-3xl -z-10" />
          </div>
        </div>
      </div>

      {/* Fold: scroll indicator flowing into next section */}
      <div className="relative">
        <div className="absolute inset-x-0 bottom-0 h-px bg-slate-800/30" />

        {/* Scroll indicator — from sm only (2026-10-14): on a phone it sits
            right under the filter card's own "Bekijk resultaten", which does
            the same thing, and the two stacked read as one step asked twice. */}
        <div className="relative z-10 hidden sm:flex flex-col items-center pb-3 md:pb-8 gap-2">
          <button
            onClick={() => {
              trackEvent('hero_cta_click', {
                variant: 'scroll_indicator',
                surface: SURFACES.hero,
                language,
              });
              scrollToCompare();
            }}
            className="flex flex-col items-center gap-2 text-fg-muted hover:text-emerald-500 transition-colors group focus-ring rounded-xl"
          >
            <span className="text-[10px] font-black uppercase tracking-[0.3em] opacity-90 group-hover:opacity-100 transition-opacity">
              {t('app.go_to_comparison_label')}
            </span>
            <div className="md:motion-safe:animate-nudge-down">
              <ChevronDown className="w-5 h-5" />
            </div>
          </button>
        </div>
      </div>
    </section>
  );
};
